import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Divider, Loader, TextInput } from '@mantine/core';
import NumberFormat from 'react-number-format';
import ResponsiveModal from '../../common/ResponsiveModal';
import FormSection from '../../common/FormSection';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { triggerNotification } from '../../../helpers/notificationHelper';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import RecordPaymentPriceItem from '../financials/RecordPaymentPriceItem';
import AppTitle from '../../common/AppTitle';

const MerchPackageOrderRefundModal = ({
  isOpen,
  onClose,
  onRefundComplete,
  regCartMerchPackage
}) => {
  const fetchedPkRegCartMerchPackage = useRef(null);
  const {
    state,
    fetchRegCartMerchPackageBalance,
    refundRegCartMerchPackage
  } = useContext(RegistrationAdminContext);
  const [formState, setFormState] = useState({
    isPartialRefund: false,
    partialRefundAmount: 0,
    showConfirmRefund: false,
    isLoading: false
  });
  const isFetching =
    !fetchedPkRegCartMerchPackage.current ||
    state.regCartMerchPackageBalance.loading;

  const { balance, merchPackage, regCart } =
    state.regCartMerchPackageBalance.value || {};

  const availableRefundAmount = balance
    ? balance.totalInCents -
      balance.refundServiceFee -
      balance.refundedAmountInCents
    : 0;

  const currency = balance?.currency?.toUpperCase();

  useEffect(() => {
    if (isOpen) {
      setFormState({
        isPartialRefund: false,
        partialRefundAmount: 0,
        showConfirmRefund: false,
        isLoading: false
      });
      if (
        fetchedPkRegCartMerchPackage.current !==
        regCartMerchPackage.pkRegCartMerchPackage
      ) {
        fetchRegCartMerchPackageBalance(
          regCartMerchPackage.pkRegCartMerchPackage,
          null,
          triggerNotification
        );
        fetchedPkRegCartMerchPackage.current =
          regCartMerchPackage.pkRegCartMerchPackage;
      }
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size={600}
      title={<AppTitle order={2}>Refund Merch Package Order</AppTitle>}
    >
      {isFetching ? (
        <AppStack
          style={{
            padding: 40,
            paddingTop: 60,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Loader color="dark" size={60} />
        </AppStack>
      ) : formState.showConfirmRefund ? (
        <FormSection
          isLoading={formState.isLoading}
          onCancel={() =>
            setFormState({
              ...formState,
              showConfirmRefund: false
            })
          }
          onSubmit={() => {
            setFormState({
              ...formState,
              isLoading: true
            });
            refundRegCartMerchPackage(
              regCartMerchPackage.pkRegCartMerchPackage,
              {
                partialRefundAmount: formState.isPartialRefund
                  ? formState.partialRefundAmount
                  : null
              },
              () => {
                triggerNotification(
                  'Merch Package Order Refunded',
                  'Success',
                  'green'
                );
                onRefundComplete();
              },
              (error) => {
                triggerNotification(error);
                setFormState({
                  ...formState,
                  isLoading: false
                });
              }
            );
          }}
          submitColor="red"
          submitTitle="Refund"
        >
          <AppStack style={{ marginTop: 20 }}>
            <AppText style={{ textAlign: 'center' }}>
              Are you sure you want to refund this merch package order?
            </AppText>
          </AppStack>
        </FormSection>
      ) : (
        <FormSection
          isLoading={formState.isLoading}
          onCancel={onClose}
          onSubmit={() => {
            setFormState({
              ...formState,
              showConfirmRefund: true
            });
          }}
          submitColor="red"
          submitTitle="Refund"
        >
          <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
            <AppText style={{ color: 'dodgerblue', fontSize: 18 }} weight={700}>
              Overview
            </AppText>
            <Divider size={2} />

            <RecordPaymentPriceItem
              currency={currency}
              label="Merch Cost"
              value={merchPackage.price / 100}
            />

            <RecordPaymentPriceItem
              currency={currency}
              label="Transaction Total"
              value={balance.totalInCents / 100}
            />

            {balance.refundServiceFee > 0 && (
              <RecordPaymentPriceItem
                color="#FA5252"
                currency={currency}
                label="Service Fee"
                value={balance.refundServiceFee / 100}
              />
            )}

            <RecordPaymentPriceItem
              color="#FA5252"
              currency={currency}
              label="Amount Refunded"
              value={balance.refundedAmountInCents / 100}
            />

            <RecordPaymentPriceItem
              currency={currency}
              isDividerHidden
              label="Refundable Balance"
              value={availableRefundAmount / 100}
            />
          </AppStack>

          <AppStack style={{ gap: 10 }}>
            <Checkbox
              checked={formState.isPartialRefund}
              label="Partial Refund"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  isPartialRefund: e.currentTarget.checked,
                  partialRefundAmount:
                    formState.partialRefundAmount === 0
                      ? availableRefundAmount
                      : formState.partialRefundAmount
                })
              }
              style={{ fontWeight: 500 }}
            />
            {formState.isPartialRefund && (
              <NumberFormat
                allowNegative={false}
                customInput={TextInput}
                decimalScale={2}
                disabled={formState.isLoading}
                fixedDecimalScale
                label="Partial Refund Amount"
                onValueChange={(values) =>
                  setFormState({
                    ...formState,
                    partialRefundAmount:
                      values.floatValue * 100 > availableRefundAmount
                        ? availableRefundAmount
                        : values.floatValue * 100
                  })
                }
                prefix="$"
                required
                rightSection={
                  balance.currency && (
                    <AppText
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#999',
                        paddingRight: 5
                      }}
                    >
                      {balance.currency?.toUpperCase()}
                    </AppText>
                  )
                }
                thousandSeparator
                value={
                  formState.partialRefundAmount
                    ? formState.partialRefundAmount / 100
                    : 0
                }
              />
            )}
          </AppStack>
        </FormSection>
      )}
    </ResponsiveModal>
  );
};

MerchPackageOrderRefundModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onRefundComplete: PropTypes.func,
  regCartMerchPackage: PropTypes.object
};

export default MerchPackageOrderRefundModal;
